import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase-config';
import html2canvas from 'html2canvas';

/** Firestore ~1 MiB doc limit — keep screenshot payload headroom for other fields */
const MAX_SCREENSHOT_DATA_URL_CHARS = 850_000;

/**
 * Re-encode JPEG smaller if the data URL is too large for Firestore.
 * @param {string} dataUrl
 * @param {number} maxEdge max width or height in px
 * @param {number} quality jpeg 0..1
 */
function downscaleJpegDataUrl(dataUrl, maxEdge, quality) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        const scale = Math.min(1, maxEdge / Math.max(width, height));
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));
        const c = document.createElement('canvas');
        c.width = width;
        c.height = height;
        const ctx = c.getContext('2d');
        if (!ctx) {
          resolve(dataUrl);
          return;
        }
        ctx.drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL('image/jpeg', quality));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = () => reject(new Error('Image decode failed'));
    img.src = dataUrl;
  });
}

const KEYWORD_GROUPS = {
  auth: ['login', 'log in', 'password', 'reset', 'signup', 'sign up', 'auth', 'permission'],
  dashboard: ['dashboard', 'sidebar', 'tab', 'widget', 'screen', 'page'],
  applications: ['application', 'applicant', 'realtor', 'owner', 'tenant'],
  payments: ['payment', 'stripe', 'invoice', 'estimate', 'charge', 'payout'],
  notifications: ['email', 'notification', 'sms', 'invite', 'message'],
  mobile: ['mobile', 'phone', 'responsive', 'tablet', 'ios', 'android'],
  performance: ['slow', 'lag', 'delay', 'loading', 'freeze', 'performance'],
};

function detectTags(text) {
  const haystack = text.toLowerCase();
  return Object.entries(KEYWORD_GROUPS)
    .filter(([, keywords]) => keywords.some((keyword) => haystack.includes(keyword)))
    .map(([tag]) => tag);
}

function detectType(category, text) {
  const haystack = text.toLowerCase();
  if (category === 'bug' || /(bug|broken|error|issue|fail|not work|doesn't work|cannot|can't)/.test(haystack)) {
    return 'defect';
  }
  if (category === 'feature' || /(feature|add|new option|request)/.test(haystack)) {
    return 'enhancement';
  }
  if (category === 'improvement' || /(improve|better|easier|simpler|cleaner)/.test(haystack)) {
    return 'improvement';
  }
  return 'general';
}

function detectPriority(text) {
  const haystack = text.toLowerCase();
  if (/(urgent|blocked|can't|cannot|broken|error|fails|missing|insufficient permissions)/.test(haystack)) {
    return 'high';
  }
  if (/(slow|confusing|hard|annoying|inconvenient)/.test(haystack)) {
    return 'medium';
  }
  return 'low';
}

function buildSuggestedAction(type, tags) {
  if (type === 'defect') {
    return 'Reproduce the issue, capture the affected user role and route, and verify whether this is a frontend bug, rules issue, or Cloud Function gap.';
  }
  if (tags.includes('auth')) {
    return 'Review authentication flow, authorized domains, and any related Firebase Auth errors.';
  }
  if (tags.includes('payments')) {
    return 'Check Stripe/payment logs, confirm callable responses, and verify payment state transitions.';
  }
  if (type === 'enhancement' || type === 'improvement') {
    return 'Review demand, confirm the target workflow, and estimate whether this fits a quick UX improvement or a larger feature.';
  }
  return 'Review the report manually, confirm the affected workflow, and decide whether it is a defect, enhancement, or support question.';
}

/** Auto summary for Firestore `subject` (no separate field in the UI). */
function deriveFeedbackSubject(message) {
  const t = String(message || '').replace(/\s+/g, ' ').trim();
  if (!t) return 'Feedback';
  const m = t.match(/^.{1,120}?[.!?](?=\s|$)/);
  let s = m ? m[0].trim() : t;
  if (s.length > 100) s = `${s.slice(0, 97).trim()}…`;
  return s || 'Feedback';
}

function categorizeFeedback({ category, subject, message }) {
  const combined = `${subject} ${message}`.trim();
  const tags = detectTags(combined);
  const type = detectType(category, combined);
  const priority = detectPriority(combined);

  return {
    type,
    tags,
    priority,
    suggestedAction: buildSuggestedAction(type, tags),
    triageVersion: 1,
  };
}

function resolveDisplayName(user, userData) {
  if (user?.displayName && user.displayName !== 'null') return user.displayName;

  const role = userData?.role;

  // Owners: try firstName + lastName
  if (role === 'owner') {
    const full = [userData?.firstName, userData?.lastName].filter(Boolean).join(' ');
    if (full) return full;
  }

  // Professionals: try businessName first
  if (role === 'professional') {
    return userData?.businessName || userData?.fullName || userData?.name || user?.email || 'Unknown';
  }

  // Realtors, tenants, etc: try fullName
  return userData?.fullName || userData?.name || user?.email || 'Unknown';
}

/** When the parent already mounts this only while open (`{open && <FeedbackModal />}`), omit `isOpen` — it defaults to true. */
function FeedbackModal({ user, userData, isOpen = true, onClose }) {
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [success, setSuccess] = useState(false);

  /**
   * Captures the visible page (viewport) excluding this modal. Returns data URL for immediate use —
   * do not rely on React state in the same tick (setState is async).
   */
  const capturePageScreenshot = async () => {
    setCapturingScreenshot(true);
    setScreenshot(null);
    const viewW = Math.max(1, document.documentElement.clientWidth || window.innerWidth);
    const viewH = Math.max(1, window.innerHeight);
    try {
      const canvas = await html2canvas(document.body, {
        backgroundColor: '#f8fafc',
        scale: Math.min(0.35, 1 / Math.max(1, window.devicePixelRatio * 0.95)),
        logging: false,
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: false,
        ignoreElements: (el) => Boolean(el?.closest?.('[data-feedback-modal]')),
        scrollX: -window.scrollX,
        scrollY: -window.scrollY,
        windowWidth: viewW,
        windowHeight: viewH,
        // Avoid capturing a tall body min-height / below-the-fold empty area as a huge white band
        onclone: (clonedDoc) => {
          const root = clonedDoc.documentElement;
          const body = clonedDoc.body;
          if (root) {
            root.style.overflow = 'hidden';
            root.style.height = `${viewH}px`;
            root.style.maxHeight = `${viewH}px`;
          }
          if (body) {
            body.style.overflow = 'hidden';
            body.style.minHeight = '0';
            body.style.height = `${viewH}px`;
            body.style.maxHeight = `${viewH}px`;
            body.style.margin = '0';
          }
        },
      });

      let dataUrl = canvas.toDataURL('image/jpeg', 0.65);

      if (dataUrl.length > MAX_SCREENSHOT_DATA_URL_CHARS) {
        dataUrl = await downscaleJpegDataUrl(dataUrl, 1280, 0.55);
      }
      if (dataUrl.length > MAX_SCREENSHOT_DATA_URL_CHARS) {
        dataUrl = await downscaleJpegDataUrl(dataUrl, 960, 0.48);
      }
      if (dataUrl.length > MAX_SCREENSHOT_DATA_URL_CHARS) {
        dataUrl = await downscaleJpegDataUrl(dataUrl, 720, 0.42);
      }

      setScreenshot(dataUrl);
      return dataUrl;
    } catch (err) {
      console.error('Feedback screenshot error:', err);
      setScreenshot(null);
      return null;
    } finally {
      setCapturingScreenshot(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const screenshotData = await capturePageScreenshot();

      const displayName = resolveDisplayName(user, userData);
      const trimmedMessage = message.trim();
      const trimmedSubject = deriveFeedbackSubject(trimmedMessage);
      const triage = categorizeFeedback({ category, subject: trimmedSubject, message: trimmedMessage });

      const feedbackData = {
        category,
        normalizedCategory: triage.type,
        subject: trimmedSubject,
        message: trimmedMessage,
        userName: displayName,
        userEmail: user?.email || 'unknown@email.com',
        userRole: userData?.role || 'unknown',
        userId: user?.uid,
        status: 'new',
        source: 'in_app',
        triage,
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        pagePath: typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search || ''}` : '',
        viewport: typeof window !== 'undefined'
          ? { w: window.innerWidth, h: window.innerHeight }
          : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      if (screenshotData) {
        feedbackData.screenshot = screenshotData;
        feedbackData.screenshotCaptured = true;
      } else {
        feedbackData.screenshotCaptured = false;
        feedbackData.screenshotError = 'capture_failed_or_unavailable';
      }

      const feedbackRef = await addDoc(collection(db, 'feedback'), feedbackData);

      try {
        const processFeedbackAiTriage = httpsCallable(functions, 'processFeedbackAiTriage');
        await processFeedbackAiTriage({ feedbackId: feedbackRef.id });
      } catch (aiErr) {
        console.warn(
          'Feedback AI triage callable failed (Firestore trigger may still process):',
          aiErr?.message || aiErr
        );
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Feedback submission error:', err);
      setError(err.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return isOpen && (
    <div 
      data-feedback-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '30px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '8px', color: '#1e293b' }}>💬 Send Feedback</h2>
        <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#64748b', lineHeight: 1.45 }}>
          When you send, we automatically attach a snapshot of the page you&apos;re on (this dialog is excluded) to help us reproduce issues.
        </p>

        {(capturingScreenshot || screenshot) && (
          <div style={{
            background: capturingScreenshot ? '#fef3c7' : '#d1fae5',
            border: `1px solid ${capturingScreenshot ? '#fbbf24' : '#6ee7b7'}`,
            borderRadius: '6px',
            padding: '10px',
            marginBottom: '16px',
            fontSize: '13px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {capturingScreenshot ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    flexShrink: 0,
                    border: '2px solid #f59e0b',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                  }} />
                  Capturing screenshot of current page...
                </>
              ) : (
                <>📸 Screenshot captured successfully</>
              )}
            </div>
            {screenshot && !capturingScreenshot ? (
              <img
                src={screenshot}
                alt="Screenshot preview"
                style={{
                  display: 'block',
                  width: '100%',
                  maxHeight: '160px',
                  marginTop: '10px',
                  objectFit: 'contain',
                  objectPosition: 'top center',
                  borderRadius: '6px',
                  border: '1px solid #a7f3d0',
                  background: '#fff',
                }}
              />
            ) : null}
          </div>
        )}

        {success ? (
          <div style={{
            background: '#d1fae5',
            border: '1px solid #6ee7b7',
            borderRadius: '8px',
            padding: '16px',
            color: '#065f46',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontWeight: 500 }}>✓ Thank you for your feedback!</p>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
              We&apos;ll review it soon. Your message and page snapshot were saved successfully.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* Category */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  cursor: 'pointer'
                }}
              >
                <option value="general">General Feedback</option>
                <option value="bug">Bug Report</option>
                <option value="feature">Feature Request</option>
                <option value="improvement">Improvement Suggestion</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Message */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#333' }}>
                Message <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#64748b', lineHeight: 1.45 }}>
                A short subject line is generated automatically from your message for our team (you don&apos;t need to add one).
              </p>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell us what you think..."
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  minHeight: '120px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                padding: '10px',
                marginBottom: '16px',
                color: '#991b1b',
                fontSize: '13px'
              }}>
                {error}
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#333',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: loading ? '#cbd5e1' : '#1e293b',
                  color: 'white',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Sending...' : 'Send Feedback'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default FeedbackModal;
